/**
 * Legal Pages API Service
 * Connects to the Talenty backend to fetch AI-generated legal page content
 * Follows the same pattern as homepage and job page APIs
 */

import { getCompanyFromUrl } from '../utils/company';

// Legal page content interfaces
export interface LegalPageSection {
  title: string;
  content: string;
  order: number;
}

export interface ContactInfo {
  company: string;
  address: string[];
  phone?: string;
  email: string;
  website?: string;
  taxId?: string;
  commercialRegister?: string;
  representative?: string;
}

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category?: string;
  order: number;
}

export interface FAQCategory {
  name: string;
  items: FAQItem[];
}

// Complete legal page content bundle interfaces
export interface FAQPageContent {
  title: string;
  subtitle?: string;
  categories: FAQCategory[];
  contactInfo?: {
    title: string;
    description: string;
    email: string;
    phone?: string;
  };
  generatedAt: string;
  mandantId?: string;
}

export interface ImpressumPageContent {
  title: string;
  contactInfo: ContactInfo;
  sections: LegalPageSection[];
  generatedAt: string;
  mandantId?: string;
}

export interface DatenschutzPageContent {
  title: string;
  lastUpdated: string;
  sections: LegalPageSection[];
  contactInfo?: ContactInfo;
  generatedAt: string;
  mandantId?: string;
}

export interface DatenschutzBewerberPageContent {
  title: string;
  lastUpdated: string;
  sections: LegalPageSection[];
  contactInfo?: ContactInfo;
  generatedAt: string;
  mandantId?: string;
}

export interface SocialMediaLink {
  id: string;
  platform: 'linkedin' | 'twitter' | 'instagram' | 'xing' | 'facebook' | 'youtube';
  url: string;
  order: number;
}

export interface SocialMediaLinks {
  links: SocialMediaLink[];
  description?: string;
  socialHeading?: string;
  generatedAt: string;
  mandantId?: string;
}

class LegalPagesApiService {
  private baseUrl: string;
  private companySlug: string;

  constructor() {
    // Configure API base URL via env with safe fallback to production backend
    this.baseUrl = import.meta.env.VITE_API_BASE_URL ||
      `https://ts.talenty.ai`;


    // Determine company from subdomain first, then path
    this.companySlug = getCompanyFromUrl();
  }


  /**
   * Get FAQ page content
   */
  async getFAQContent(): Promise<FAQPageContent> {
    const url = `${this.baseUrl}/${this.companySlug}/legal/faq`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Die FAQ-Inhalte konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get Impressum page content
   */
  async getImpressumContent(): Promise<ImpressumPageContent> {
    const url = `${this.baseUrl}/${this.companySlug}/legal/impressum`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Das Impressum konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get Datenschutz page content
   */
  async getDatenschutzContent(): Promise<DatenschutzPageContent> {
    const url = `${this.baseUrl}/${this.companySlug}/legal/datenschutz`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Die Datenschutzerklärung konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get Datenschutz für Bewerber page content
   */
  async getDatenschutzBewerberContent(): Promise<DatenschutzBewerberPageContent> {
    const url = `${this.baseUrl}/${this.companySlug}/legal/datenschutz-bewerber`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Die Datenschutzhinweise für Bewerber konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get social media links for the footer
   */
  async getSocialMediaLinks(): Promise<SocialMediaLinks> {
    const url = `${this.baseUrl}/${this.companySlug}/legal/social-media`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Die Social-Media-Links konnten nicht geladen werden (Status ${response.status}).`);
    }

    return response.json();
  }

  /**
   * Test API connectivity
   */
  async testConnection(): Promise<{ message: string; timestamp: string; pages: string[] }> {
    const response = await fetch(`${this.baseUrl}/legal/test`);

    if (!response.ok) {
      throw new Error(`Der Dienst für rechtliche Inhalte ist nicht erreichbar (Status ${response.status}).`);
    }

    return response.json();
  }
}

export const legalPagesApi = new LegalPagesApiService();
